import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  BookOpen,
  ClipboardList,
  HelpCircle,
  LogOut,
  KeyRound,
  GraduationCap,
  FolderOpen,
  ShieldCheck,
  BarChart3,
  Receipt,
  HeartPulse,
  Bot,
  Euro,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import CoursesManager from "@/components/CoursesManager";
import AttendanceList from "@/components/AttendanceList";
import QuestionsManager from "@/components/QuestionsManager";
import PricesManager from "@/components/PricesManager";
import InstructorsManager from "@/components/InstructorsManager";
import GestoresManager from "@/components/GestoresManager";
import LiquidacionManager from "@/components/LiquidacionManager";
import InvoicingManager from "@/components/InvoicingManager";
import MasksManager from "@/components/MasksManager";
import ReceptionManager from "@/components/ReceptionManager";
import DriveManager from "@/components/DriveManager";
import { MaskRunoutAlert } from "@/components/MaskRunoutAlert";
import { MailboxAlertBanner } from "@/components/MailboxAlertBanner";
import { adminApi, clearSession, getUsername } from "@/lib/auth";
import { DEPLOY_INFO } from "@/config/deployInfo";
import { toast } from "sonner";

export default function AdminScreen() {
  const navigate = useNavigate();
  const username = getUsername() || "";
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [unreviewed, setUnreviewed] = useState(0);
  const [tab, setTab] = useState("courses");

  useEffect(() => {
    adminApi
      .get("/auth/me-admin")
      .then((res) => {
        // Si el gestor entró con una temporal, obligamos a cambiarla.
        if (res.data?.must_change_password) {
          navigate("/admin/cambiar-contrasena", { replace: true });
          return;
        }
        setIsSuperAdmin(!!res.data?.is_super_admin);
      })
      .catch(() => setIsSuperAdmin(false));
    adminApi
      .get("/admin/reception")
      .then((res) => setUnreviewed(res.data?.unreviewed || 0))
      .catch(() => setUnreviewed(0));
  }, [navigate]);

  const handleLogout = () => {
    clearSession();
    toast.success("Sesión cerrada.");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA]" data-testid="admin-screen">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <MailboxAlertBanner onGoToReception={() => setTab("reception")} />
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/", { state: { fromAdmin: true } })}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
            data-testid="admin-home-button"
          >
            <ChevronLeft className="h-4 w-4" />
            Inicio
          </button>
          <div className="flex items-center gap-4">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 hidden sm:block">
              Gestor · {username}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/admin/cambiar-contrasena")}
              className="h-9 border border-zinc-300 hover:bg-zinc-50"
              data-testid="admin-change-password-button"
              title="Cambiar contraseña"
            >
              <KeyRound className="h-3.5 w-3.5 mr-1.5" />
              <span className="hidden sm:inline">Contraseña</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleLogout}
              className="h-9 border border-zinc-300 hover:bg-zinc-50"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
            <span className="inline-block h-2 w-2 bg-black" />
            Menú de Gestión
            <span
              className="normal-case tracking-normal text-zinc-600 font-medium"
              title="Número y fecha del último despliegue"
              data-testid="admin-deploy-info"
            >
              ({DEPLOY_INFO.number} · {DEPLOY_INFO.date})
            </span>
          </div>
          <h1 className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]">
            Cursos, asistencias y preguntas
          </h1>
          <p className="mt-3 text-zinc-600">
            Administra los cursos activos, consulta las firmas recogidas y
            mantén el banco de preguntas para los exámenes.
          </p>
        </div>

        <MaskRunoutAlert onGoToMasks={() => setTab("masks")} />
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-zinc-200 rounded-md p-1 h-auto flex-wrap">
            <TabsTrigger
              value="courses"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-courses"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Cursos
            </TabsTrigger>
            <TabsTrigger
              value="attendance"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-attendance"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              Asistencias
            </TabsTrigger>
            <TabsTrigger
              value="questions"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-questions"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Preguntas
            </TabsTrigger>
            <TabsTrigger
              value="instructors"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-instructors"
            >
              <GraduationCap className="h-4 w-4 mr-2" />
              Instructores
            </TabsTrigger>
            <TabsTrigger
              value="prices"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-prices"
            >
              <Euro className="h-4 w-4 mr-2" />
              Precios
            </TabsTrigger>
            <TabsTrigger
              value="liquidacion"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-liquidacion"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Liquidación
            </TabsTrigger>
            <TabsTrigger
              value="invoicing"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-invoicing"
            >
              <Receipt className="h-4 w-4 mr-2" />
              Facturación
            </TabsTrigger>
            <TabsTrigger
              value="reception"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-reception"
            >
              <Bot className="h-4 w-4 mr-2" />
              Recepción
              {unreviewed > 0 && (
                <span
                  className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[11px] font-bold text-white"
                  data-testid="reception-unreviewed-badge"
                >
                  {unreviewed}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="masks"
              className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
              data-testid="tab-masks"
            >
              <HeartPulse className="h-4 w-4 mr-2" />
              Mascarillas
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger
                value="gestores"
                className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white text-zinc-700 rounded-sm h-10 px-4 text-sm font-semibold transition-colors"
                data-testid="tab-gestores"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Gestores
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="courses" className="mt-6">
            <CoursesManager />
          </TabsContent>
          <TabsContent value="attendance" className="mt-6">
            <AttendanceList />
          </TabsContent>
          <TabsContent value="questions" className="mt-6">
            <QuestionsManager />
          </TabsContent>
          <TabsContent value="instructors" className="mt-6">
            <InstructorsManager />
          </TabsContent>
          <TabsContent value="prices" className="mt-6">
            <PricesManager />
          </TabsContent>
          <TabsContent value="liquidacion" className="mt-6">
            <LiquidacionManager />
          </TabsContent>
          <TabsContent value="invoicing" className="mt-6">
            <InvoicingManager />
          </TabsContent>
          <TabsContent value="reception" className="mt-6 space-y-8">
            <ReceptionManager onUnreviewedChange={setUnreviewed} />
            <DriveManager />
          </TabsContent>
          <TabsContent value="masks" className="mt-6">
            <MasksManager />
          </TabsContent>
          {isSuperAdmin && (
            <TabsContent value="gestores" className="mt-6">
              <GestoresManager />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Sticky bottom bar — DOCUMENTACIÓN access */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 border-t border-zinc-200 bg-white/95 backdrop-blur"
        data-testid="admin-bottom-bar"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-3 flex items-center justify-center">
          <Button
            onClick={() => navigate("/admin/documentation")}
            className="h-12 px-6 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md font-semibold"
            data-testid="admin-documentation-button"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            DOCUMENTACIÓN
          </Button>
        </div>
      </div>
      <div className="h-20" aria-hidden />
    </div>
  );
}
